import {
  SimpleShowLayout,
  Labeled,
  useRecordContext,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState, useCallback } from 'react';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import { CopiableField } from '@/components/CopiableIdField';
import { RawDataButton } from '@/components/RawDataButton';

export const ConversionMonitorCommandPanel = () => {
  const record = useRecordContext();
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const onOpenHistoryDialog = useCallback(() => {
    setHistoryDialogOpen(true);
  }, []);

  const onCloseHistoryDialog = useCallback(() => {
    setHistoryDialogOpen(false);
  }, []);

  if (!record) {
    return null;
  }

  const {
    trialMetrics,
    offlineAnalysis,
    violationSeverity,
    conversionStatus,
    freeTrial,
  } = record;

  return (
    <SimpleShowLayout>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="h6">Therapist</Typography>
          <RawDataButton
            entity={record}
            title={`Raw Conversion Monitor Data: ${record.id}`}
          />
        </Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Labeled label="Therapist Id">
              <CopiableField
                targetNavigationResource="therapists"
                targetNavigationFilters={{
                  _id: String(record.id),
                  isHiddenTestAccount: 'visible',
                }}
                content={String(record.id)}
                copyLink
              />
            </Labeled>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>
          Trial Metrics
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Labeled label="Trial Limit">
              <Typography>{trialMetrics?.limit || 0}</Typography>
            </Labeled>
          </Grid>
          <Grid item xs={6}>
            <Labeled label="Trial Usage">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>{trialMetrics?.usage || 0}</Typography>
                {freeTrial?.history &&
                  freeTrial.history.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={onOpenHistoryDialog}
                      color="primary"
                      sx={{ padding: '4px' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  )}
              </Box>
            </Labeled>
          </Grid>
          <Grid item xs={6}>
            <Labeled label="Committed Notes">
              <Typography>
                {trialMetrics?.committedNotes || 0}
              </Typography>
            </Labeled>
          </Grid>
          <Grid item xs={6}>
            <Labeled label="Notes Beyond Limit">
              <Typography
                sx={{
                  color:
                    trialMetrics?.notesBeyondLimit > 0
                      ? 'error.main'
                      : 'inherit',
                  fontWeight:
                    trialMetrics?.notesBeyondLimit > 0
                      ? 'bold'
                      : 'normal',
                }}
              >
                {trialMetrics?.notesBeyondLimit || 0}
              </Typography>
            </Labeled>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>
          Offline Analysis
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Labeled label="Offline Percentage">
              <Typography>
                {offlineAnalysis?.offlinePercentage
                  ? `${offlineAnalysis.offlinePercentage.toFixed(1)}%`
                  : 'N/A'}
              </Typography>
            </Labeled>
          </Grid>
          <Grid item xs={6}>
            <Labeled label="Suspected Offline Notes">
              <Typography>
                {offlineAnalysis?.suspectedOfflineNotes?.count || 0}
              </Typography>
            </Labeled>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>
          Violation Severity
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Labeled label="Severity Level">
              <Chip
                label={violationSeverity?.level || 'none'}
                color={
                  violationSeverity?.level === 'critical'
                    ? 'error'
                    : violationSeverity?.level === 'moderate'
                      ? 'warning'
                      : violationSeverity?.level === 'acceptable'
                        ? 'success'
                        : 'default'
                }
              />
            </Labeled>
          </Grid>
          <Grid item xs={6}>
            <Labeled label="Needs Investigation">
              <Typography>
                {violationSeverity?.needsInvestigation ? 'Yes' : 'No'}
              </Typography>
            </Labeled>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>
          Conversion Status
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Labeled label="Status">
              <Typography>
                {conversionStatus?.status || 'Unknown'}
              </Typography>
            </Labeled>
          </Grid>
          <Grid item xs={6}>
            <Labeled label="Is Subscribed">
              <Typography>
                {conversionStatus?.isSubscribed ? 'Yes' : 'No'}
              </Typography>
            </Labeled>
          </Grid>
          {conversionStatus?.firstSubscribedAt && (
            <Grid item xs={6}>
              <Labeled label="First Subscribed At">
                <Typography>
                  {new Date(
                    conversionStatus.firstSubscribedAt,
                  ).toLocaleString()}
                </Typography>
              </Labeled>
            </Grid>
          )}
        </Grid>

        {freeTrial?.history && freeTrial.history.length > 0 && (
          <Typography variant="h6" gutterBottom>
            Free Trial History
          </Typography>
        )}
      </Box>

      <Dialog
        open={historyDialogOpen}
        onClose={onCloseHistoryDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Free Trial Notes Usage History</DialogTitle>
        <DialogContent>
          <Box sx={{ padding: '1rem' }}>
            {freeTrial?.history?.map((note: any) => (
              <Box key={note._id} display="flex" marginBottom="1rem">
                <Labeled label="Id">
                  <CopiableField
                    targetNavigationResource="therapistSessionNotes"
                    targetNavigationFilters={{
                      _id: { $oid: note._id },
                      isHiddenTestAccount: 'visible',
                    }}
                    content={note._id}
                  />
                </Labeled>
                <Labeled marginLeft="1rem" label="Date">
                  <GenericRelativeDateDisplay date={note.when} />
                </Labeled>
                <Labeled marginLeft="1rem" label="Entry Type">
                  <Chip label={note.entryType} />
                </Labeled>
                <Labeled marginLeft="1rem" label="Media Type">
                  <Chip label={note.mediaType} />
                </Labeled>
                <Labeled marginLeft="1rem" label="Word Count">
                  <Typography>{note.wordCount}</Typography>
                </Labeled>
                <Labeled marginLeft="1rem" label="Trial Notes Left">
                  <Typography>{note.notesLeft}</Typography>
                </Labeled>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </SimpleShowLayout>
  );
};
